import type { ApiRequestHelpers } from "./useHttpClient";
import type { EmptyResponse, Note, Response, TreatmentIdentifier } from "~/types/api";

export function createNoteApi({ getRequest, postRequest, patchRequest, deleteRequest }: ApiRequestHelpers) {
  /** Fetches all notes for a treatment. */
  const treatmentNotes = (treatmentId: TreatmentIdentifier) =>
    getRequest<Response<Note[]>>(`/treatments/${treatmentId}/notes`);

  /** Creates a new note on a treatment. */
  const createTreatmentNote = (treatmentId: TreatmentIdentifier, content: string) =>
    postRequest<Response<Note>>(`/treatments/${treatmentId}/notes`, { content });

  /** Updates the content of a treatment note. */
  const updateTreatmentNote = (treatmentId: TreatmentIdentifier, noteId: string, content: string) =>
    patchRequest<Response<Note>>(`/treatments/${treatmentId}/notes/${noteId}`, { content });

  /** Deletes a note from a treatment. */
  const deleteTreatmentNote = (treatmentId: TreatmentIdentifier, noteId: string) =>
    deleteRequest<EmptyResponse>(`/treatments/${treatmentId}/notes/${noteId}`);

  /** Toggles the flagged status of a treatment note. */
  const toggleTreatmentNoteFlag = (treatmentId: TreatmentIdentifier, noteId: string) =>
    postRequest<Response<Note>>(`/treatments/${treatmentId}/notes/${noteId}/toggle-flag`);

  /** Fetches all notes for a patient. */
  const patientNotes = (patientId: number) => getRequest<Response<Note[]>>(`/patients/${patientId}/notes`);

  /** Fetches only flagged notes for a patient. */
  const flaggedPatientNotes = (patientId: number) =>
    getRequest<Response<Note[]>>(`/patients/${patientId}/notes/flagged`);

  /** Creates a new note on a patient. */
  const createPatientNote = (patientId: number, content: string) =>
    postRequest<Response<Note>>(`/patients/${patientId}/notes`, { content });

  /** Updates the content of a patient note. */
  const updatePatientNote = (patientId: number, noteId: string, content: string) =>
    patchRequest<Response<Note>>(`/patients/${patientId}/notes/${noteId}`, { content });

  /** Deletes a note from a patient. */
  const deletePatientNote = (patientId: number, noteId: string) =>
    deleteRequest<EmptyResponse>(`/patients/${patientId}/notes/${noteId}`);

  /** Toggles the flagged status of a patient note. */
  const togglePatientNoteFlag = (patientId: number, noteId: string) =>
    postRequest<Response<Note>>(`/patients/${patientId}/notes/${noteId}/toggle-flag`);

  return {
    treatmentNotes,
    createTreatmentNote,
    updateTreatmentNote,
    deleteTreatmentNote,
    toggleTreatmentNoteFlag,
    patientNotes,
    flaggedPatientNotes,
    createPatientNote,
    updatePatientNote,
    deletePatientNote,
    togglePatientNoteFlag,
  };
}

export type NoteApi = ReturnType<typeof createNoteApi>;
